import { ReactNode, forwardRef } from "react";
import { Field } from "./Field";
import { Input } from "./Input";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { Placeholder } from "../Textbox/Placeholder";
import { InvalidValue } from "../InvalidValue";
import { withSubComponents } from "../../utilities/withSubComponents";

/**
 * The `Datebox` component properties.
 */
export interface DateboxProps extends InputBaseProps<Date | InvalidValue | null> {

  /**
   * The timezone the value is associated with. If not specified defaults to `"utc"`.
   * When set to `"utc"`, the time portion of dates is normalized as being at midnight UTC.
   * When set to `"local"`, the time portion of dates is normalized as being at midnight in the current user's timezone offset.
   */
  readonly zone?: "utc" | "local";

  /**
   * Content to show prior to the date.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show after the date.
   */
  readonly suffix?: ReactNode;

  /**
   * The datebox tooltip.
   */
  readonly tooltip?: ReactNode;
}

/**
 * The `Datebox` component sub components.
 */
export interface DateboxSubComponents {
  /**
   * A React Hook Form compatible Datebox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Datebox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a date.
 */
export const Datebox = withSubComponents<RefComponent<HTMLInputElement, DateboxProps>, DateboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, DateboxProps>(function Datebox(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
